/**
 * One market indicator: a number out of a hundred, a word, and a rail.
 *
 * The rail is a bar and not a dial. A speedometer spends most of its area on
 * decoration and reads no faster than a line does; three of them stacked in a
 * column would take the height this whole panel has.
 *
 * The word beside the number is not a repetition of it. A reader who cannot
 * separate the colours still has to be able to tell a hostile market from a
 * calm one, and 31 versus 74 is not something anybody reads at a glance.
 *
 * Tone is asked for by the caller rather than derived from the score, because
 * the two indicators run in opposite directions: 80 is good for a recovery
 * climate and bad for market risk, and a component that guessed would be
 * wrong exactly half the time.
 */

export type GaugeTone = "good" | "neutral" | "caution" | "bad" | "unknown";

const RAIL: Record<GaugeTone, string> = {
  good: "bg-status-positive",
  neutral: "bg-action-primary",
  caution: "bg-status-warning",
  bad: "bg-status-negative",
  unknown: "bg-border-strong",
};

const WORD: Record<GaugeTone, string> = {
  good: "text-status-positive",
  neutral: "text-text-primary",
  caution: "text-status-warning",
  bad: "text-status-negative",
  unknown: "text-text-muted",
};

type Props = {
  readonly label: string;
  /** Null when there is no reading, which is a state and not a zero. */
  readonly score: number | null;
  readonly band: string | null;
  readonly tone: GaugeTone;
  /** Why there is no reading, shown in place of the rail when there is none. */
  readonly absent?: string | undefined;
  readonly title?: string | undefined;
  readonly testId?: string | undefined;
};

export function Gauge({
  label,
  score,
  band,
  tone,
  absent,
  title,
  testId,
}: Props): React.JSX.Element {
  const filled = score === null ? 0 : Math.max(0, Math.min(100, score));

  return (
    <div className="flex flex-col gap-1" data-testid={testId} title={title}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-label-caps text-text-secondary">{label}</span>
        {/* The scale, printed. A bare 31 is unreadable without knowing what
            it is out of and which direction is good; the rail shows the first
            and the word below shows the second. */}
        <span className={`font-numeric text-body-main tabular-nums ${WORD[tone]}`}>
          {score === null ? "—" : score}
          {score !== null && <span className="text-helper-sm text-text-muted">/100</span>}
        </span>
      </div>

      {score === null ? (
        <p className="text-helper-sm text-text-muted">{absent ?? "Нет данных"}</p>
      ) : (
        <>
          <span
            aria-hidden="true"
            className="flex h-1.5 w-full overflow-hidden rounded-row bg-surface-selected"
          >
            <span
              className={`block h-full ${RAIL[tone]}`}
              style={{ width: `${String(filled)}%` }}
            />
          </span>
          <span className={`text-helper-sm ${WORD[tone]}`}>{band ?? ""}</span>
        </>
      )}
    </div>
  );
}
